import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { useTypedSelector } from '@Features/store';
import { TableCell } from '@mui/material';
import { usePageLoader } from '@Providers/LoadingProvider';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { studentsExportData, studentsGetAll } from '../Slice/Students.Slice';

const TABLE_HEADS = [
	{ displayName: 'username', fieldName: 'username' },
	// One Name column instead of separate first/last. Sorts on first_name —
	// the only half the backend can order by, since sortBy is applied to the
	// users table directly.
	{ displayName: 'name', fieldName: 'first_name' },
	{ displayName: 'phone number', fieldName: 'phone_number' },
	// Lives on the joined students table, which the sort helper cannot prefix.
	{ displayName: 'parent phone', fieldName: 'parent_phone', canSort: false },
	{ displayName: 'email', fieldName: 'email' },
	{ displayName: 'registered on', fieldName: 'created_at' },
];

/** Either half can be missing, so join on what is there rather than printing a
 *  stray space or the word "undefined". */
const fullName = (student: { first_name?: string; last_name?: string }) =>
	[student.first_name, student.last_name].filter(Boolean).join(' ') || '-';

type Props = {
	setBar: any;
};
const StudentsTable: React.FC<Props> = ({ setBar }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { setIsLoading } = usePageLoader();
	const auth = useTypedSelector((state) => state.auth);
	const {
		fetchAll,
		loaders,
		meta,
		requestStatus,
		filters,
		errors,
		selectedFilters,
		dateRange,
		query,
		sortBy,
	} = useTypedSelector((state) => state.Students);
	const { displaySnackbar } = useSnackbar();

	const onPageChangeRequest = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
			dispatch(
				studentsGetAll({
					page: activePage,
					perPage: perPage ?? 10,
					sortBy,
					filters,
					query,
					from: startDate,
					to: endDate,
				})
			);
		},
		[dispatch]
	);

	const handleTableItemClick = useCallback(
		(id: string) => () => {
			history.push(`/dashboard/students/${id}`);
		},
		[history]
	);

	useEffect(() => {
		if (loaders.fetchAll === 'fulfilled') setIsLoading(true);
		setBar(true);
		if (loaders.fetchAll === 'rejected') setIsLoading(false);
	}, [loaders.fetchAll, setIsLoading]);

	useEffect(() => {
		if (loaders.export) {
			setIsLoading(true);
		}
		if (loaders.export === undefined) setIsLoading(false);
	}, [loaders.export, errors.export, setIsLoading]);
	useEffect(() => {
		if (requestStatus.export === 'DONE') {
			displaySnackbar('success', 'Exported Successfully');
		}
		if (requestStatus.export === 'FAILED') {
			displaySnackbar('error', 'Can Not Export!');
		}
	}, [displaySnackbar, requestStatus.export]);
	// A failed fetch used to be invisible: the rejected reducer keeps the
	// previous rows, so an applied filter looked applied while the table
	// silently showed the unfiltered page.
	//
	// Only a failure of a request made by THIS visit counts: the FAILED status
	// outlives the page (a stale login 401s on the first load, the app logs
	// out, and after signing in again the remounted table used to replay the
	// toast over rows that loaded fine). A 401 isn't reported either — the
	// app is already sending the user to the login page.
	const requestedThisVisit = useRef(false);
	useEffect(() => {
		if (loaders.fetchAll === 'pending') requestedThisVisit.current = true;
	}, [loaders.fetchAll]);
	useEffect(() => {
		if (requestStatus.fetchAll === 'FAILED' && requestedThisVisit.current && auth.isAuth) {
			displaySnackbar('error', "Couldn't load students — filters not applied");
		}
	}, [auth.isAuth, displaySnackbar, requestStatus.fetchAll]);

	return (
		<>
			{(auth.user.type === 'super' || auth.grants.has('exp-st')) && (
			<FlexLayout alignItems="center" justifyContent="flex-end">
				<RoundButton
					variant="contained"
					startIcon={<EdIcon>south</EdIcon>}
					// Dispatch straight from the slice's last-used args — the old
					// doExport flag only fired from the page-change callback, which
					// nothing re-triggered after the flag flipped.
					onClick={() =>
						dispatch(
							studentsExportData({
								page: meta?.current_page ?? 1,
								perPage: meta?.per_page ?? 10,
								filters: selectedFilters,
								query: query ?? '',
								sortBy,
								from: dateRange.from,
								to: dateRange.to,
							})
						)
					}
				>
					Export
				</RoundButton>
			</FlexLayout>
			)}
			<MainTable
				total={meta?.total ?? 0}
				dateFilter
				pageNum={meta?.current_page}
				tableFilters={filters}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				RowsperPage={Number(meta?.per_page)}
				$hasActions
				onPageChangeRequest={onPageChangeRequest}
				renderItems={() => {
					if (fetchAll.length === 0)
						return (
							<MainTableRow>
								<TableCell colSpan={TABLE_HEADS.length + 1}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					return fetchAll.map((student) => {
						return (
							<MainTableRow
								key={student.uuid}
								hover
								onClick={handleTableItemClick(student.uuid)}
							>
								<TableCell>{student.username}</TableCell>
								<TableCell>{fullName(student)}</TableCell>
								<TableCell>{student.phone_number || '-'}</TableCell>
								<TableCell>
									{student.student?.parent_phone || '-'}
								</TableCell>
								<TableCellStyle>{student.email || '-'}</TableCellStyle>
								<TableCell>
									<DateTimeFormatter date={student.created_at} />
								</TableCell>
								<TableCell />
							</MainTableRow>
						);
					});
				}}
			/>
		</>
	);
};

const TableCellStyle = styled(TableCell)`
	font-family: Roboto;
	font-size: 1rem;
	padding: 0 1.063rem 0;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
`;

export default StudentsTable;
